import {Component, ErrorInfo, JSX, ReactNode} from 'react';
import {styled} from 'styled-components';

interface ErrorBoundaryProps {
  fallback?: JSX.Element;
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  showDetails: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public static displayName = 'ErrorBoundary';

  public constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {hasError: false, showDetails: false};
  }

  public static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    console.error('getDerivedStateFromError', error);
    return {hasError: true, error};
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('componentDidCatch', error, errorInfo);
    this.setState({error, errorInfo});
    // Hook up logging/telemetry here if needed
  }

  private handleReload = () => {
    window.location.reload();
  };

  private toggleDetails = () => {
    this.setState(prev => ({showDetails: !prev.showDetails}));
  };

  public override render(): ReactNode {
    const {fallback, children} = this.props;
    const {hasError} = this.state;

    if (!hasError) {
      return children;
    }

    if (fallback) {
      return fallback;
    }

    return (
      <FullScreenContainer role="alert">
        <Panel>
          <Title>Something went wrong</Title>
          <Body>An unexpected error occurred. You can try reloading the page.</Body>
          <ButtonRow>
            <ButtonOutlined onClick={this.handleReload}>Reload</ButtonOutlined>
            <ButtonGhost onClick={this.toggleDetails}>
              {this.state.showDetails ? 'Hide details' : 'Show details'}
            </ButtonGhost>
          </ButtonRow>
        </Panel>
        {this.state.showDetails && (
          <Details>
            {this.state.error && (
              <DetailTitle>
                {this.state.error.name}: {this.state.error.message}
              </DetailTitle>
            )}
            {this.state.errorInfo?.componentStack && (
              <pre>{this.state.errorInfo.componentStack}</pre>
            )}
          </Details>
        )}
      </FullScreenContainer>
    );
  }
}

const FullScreenContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 10000;
`;

const Panel = styled.div`
  width: 100%;
  max-width: 720px;
  margin: 0 16px;
  background: #b00020;
  color: #ffffff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.4);
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const Body = styled.div`
  font-size: 14px;
  opacity: 0.95;
  margin-bottom: 8px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
`;

const ButtonBase = styled.button`
  height: 36px;
  border-radius: 8px;
  padding: 0 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out, color 0.15s ease-in-out, border-color 0.15s ease-in-out;
`;

const ButtonOutlined = styled(ButtonBase)`
  color: #ffffff;
  background: transparent;
  border: 1px solid rgba(255,255,255,0.7);
  &:hover {
    border-color: #ffffff;
    background: rgba(255,255,255,0.08);
  }
`;

const ButtonGhost = styled(ButtonBase)`
  color: #ffffff;
  background: transparent;
  border: none;
  &:hover {
    background: rgba(255,255,255,0.08);
  }
`;

const Details = styled.div`
  width: 100%;
  max-width: 720px;
  margin: 12px 16px 0 16px;
  background: rgba(0,0,0,0.6);
  color: #ffffff;
  border-radius: 12px;
  padding: 12px;
  pre {
    margin: 0;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 12px;
    opacity: 0.9;
  }
`;

const DetailTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
  opacity: 0.9;
`;
